import { z } from 'zod';
import { currencySchema } from './org';
import { defaultPaymentMethodSchema, paymentBillingAddressSchema } from './payment-method';
import {
  type Product,
  productDraftOptionsSchema,
  productSchema,
  type ScriptDraftOptions,
  storedProductSchema,
  type TitleDraftOptions,
} from './products';
import type { ProjectStatus } from './project';

export const ORDER_STATUSES = [
  'draft',
  'awaiting_payment',
  'placed',
  'cancelled',
  'refunded',
] as const;
export const orderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const ORDER_KINDS = ['main', 'addon'] as const;
export const PAYMENT_MODES = ['card', 'account'] as const;

/**
 * What a customer is told about an order: coarser than the staff states, so those can multiply without customers
 * seeing them. `draft` orders are not listed on the Orders page.
 */
export const CUSTOMER_ORDER_STATUSES = [
  'draft',
  'awaiting_payment',
  'in_progress',
  'delivered',
  'cancelled',
] as const;
export const customerOrderStatusSchema = z.enum(CUSTOMER_ORDER_STATUSES);
export type CustomerOrderStatus = z.infer<typeof customerOrderStatusSchema>;

/**
 * The customer wording for an order and its project. Mirrors `customer_order_status` in SQL, which is what the
 * view `customer_orders` answers with. A project state added later reads as in progress until it is given its own
 * wording here and there.
 */
export function customerOrderStatus(
  orderStatus: OrderStatus,
  projectStatus: ProjectStatus,
): CustomerOrderStatus {
  if (orderStatus === 'draft') return 'draft';
  if (orderStatus === 'awaiting_payment') return 'awaiting_payment';
  if (orderStatus === 'cancelled' || orderStatus === 'refunded') return 'cancelled';
  if (projectStatus === 'delivered') return 'delivered';
  if (projectStatus === 'cancelled') return 'cancelled';
  return 'in_progress';
}

/** Unpaid orders go back to draft after this many days. */
export const ORDER_PAYMENT_WINDOW_DAYS = 7;

const timestamp = z.iso.datetime({ offset: true });

/**
 * A row of the view `customer_orders`: all a customer may know about an order. There is no list amount, no discount
 * and no Stripe id in it, and `amount_cents` / `tax_cents` are null when `amounts_hidden`. It also carries what the
 * Orders page shows beside it: the project's title and due date, who ordered, and the status in customer wording.
 */
export const customerOrderSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  workspace_id: z.uuid(),
  organization_id: z.uuid(),
  kind: z.enum(ORDER_KINDS),
  status: orderStatusSchema,
  // an old row may still hold the retired placeholder product
  product: storedProductSchema,
  options: z.record(z.string(), z.unknown()),
  reference: z.string().nullable(),
  payment_mode: z.enum(PAYMENT_MODES),
  currency: currencySchema.nullable(),
  amount_cents: z.number().int().nonnegative().nullable(),
  tax_cents: z.number().int().nonnegative().nullable(),
  amounts_hidden: z.boolean(),
  created_by: z.uuid().nullable(),
  submitted_by: z.uuid().nullable(),
  paid_by: z.uuid().nullable(),
  submitted_at: timestamp.nullable(),
  placed_at: timestamp.nullable(),
  expires_at: timestamp.nullable(),
  cancelled_at: timestamp.nullable(),
  refunded_at: timestamp.nullable(),
  created_at: timestamp,
  /** The project's name, which follows the title the customer typed. */
  project_title: z.string(),
  /** Who submitted the order, from their profile: a name, never an email. Null on a draft. */
  ordered_by_name: z.string().nullable(),
  /** The project's due date, fixed when its main order was placed. */
  due_at: timestamp.nullable(),
  customer_status: customerOrderStatusSchema,
});
export type CustomerOrder = z.infer<typeof customerOrderSchema>;

const reference = z
  .string()
  .trim()
  .max(80)
  .nullable()
  .default(null)
  .transform((v) => v || null);

/** `POST /api/projects/:projectId/orders` and `PATCH /api/orders/:orderId`: a draft's product, options, reference. */
export const orderDraftRequestSchema = z
  .strictObject({ product: productSchema, options: z.unknown(), reference })
  .transform((v, ctx) => {
    const options = productDraftOptionsSchema(v.product).safeParse(v.options);
    if (!options.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'options do not fit the product',
      });
      return z.NEVER;
    }
    return { ...v, options: options.data };
  });
export type OrderDraftRequest = {
  product: Product;
  options: TitleDraftOptions | ScriptDraftOptions;
  reference: string | null;
};

/**
 * `POST /api/orders/:orderId/submit`: the browser's IANA time zone, kept on the order so the confirmation email
 * can show the due date as the form did.
 */
export const orderSubmitRequestSchema = z.strictObject({ timeZone: z.string().min(1).max(64) });
export type OrderSubmitRequest = z.infer<typeof orderSubmitRequestSchema>;

/** The worker answers every order route with the order as the customer view shows it. */
export const orderResponseSchema = z.object({ order: customerOrderSchema });
export type OrderResponse = z.infer<typeof orderResponseSchema>;

/**
 * `POST /api/orders/:orderId/payment`: the client secret the embedded payment form is mounted with, and whether
 * this payer is offered the chance to keep the card. Only someone who may manage the organization's billing is,
 * so every stored card is deliberately the organization's.
 */
export const createPaymentResponseSchema = z.object({
  clientSecret: z.string().min(1),
  cardSaveOffered: z.boolean(),
  /** The organization's default card, so a one-step submit can name it and confirm with it. Null when it has none. */
  defaultPaymentMethod: defaultPaymentMethodSchema.nullable(),
  /**
   * The organization's billing address, sent only when there is a default card: a one-step confirmation has no
   * address form, and the session must have an address before Stripe Tax will give a total. Null otherwise.
   */
  billingAddress: paymentBillingAddressSchema.nullable(),
});
export type CreatePaymentResponse = z.infer<typeof createPaymentResponseSchema>;

/**
 * A row of `orders` as staff see it (`staff.orders.view`): the whole snapshot, including the list amount, the
 * discount and the payment references that never reach a customer, plus the names the staff list shows.
 */
export const staffOrderSchema = customerOrderSchema
  .omit({
    amounts_hidden: true,
    workspace_id: true,
    project_title: true,
    ordered_by_name: true,
    due_at: true,
    customer_status: true,
  })
  .extend({
    workspace_id: z.uuid().nullable(),
    discount_percent: z.coerce.number().nullable(),
    list_amount_cents: z.number().int().nullable(),
    price_list_version_id: z.uuid().nullable(),
    stripe_session_id: z.string().nullable(),
    stripe_payment_intent_id: z.string().nullable(),
    organization_name: z.string(),
    project_name: z.string(),
    price_list_name: z.string().nullable(),
    price_list_version: z.number().int().nullable(),
  });
export type StaffOrder = z.infer<typeof staffOrderSchema>;
